/**
 * ============================================================================
 * useOosListQuery — Phase 7 frontend hook.
 * Owner: Phase 7 — Out-of-Specification Module.
 *
 * Wraps GET /api/v1/oos with optional status/kind/batch query params. Returns
 * a tiny query envelope { data, isLoading, isError, refetch } so the screen
 * doesn't depend on react-query's exact shape (keeps the screen testable in
 * isolation and matches the existing pattern in the codebase).
 * ============================================================================ */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';

export type OosStatusValue =
  | 'REPORTED'
  | 'PHASE_1'
  | 'PHASE_2'
  | 'CONCLUDED'
  | 'CLOSED'
  | 'CANCELLED';

export type OosKindValue = 'OUT_OF_SPEC' | 'OUT_OF_TREND' | 'OUT_OF_EXPECTATION';

export interface OosListItem {
  id: string;
  oosNumber: string;
  status: OosStatusValue;
  outOfSpecKind: OosKindValue;
  sampleRef: string;
  batchRef: string | null;
  testMethodRef: string;
  resultValue: string;
  resultUnits: string;
  reportedAtUtc: string;
  reporterUserId: string;
  createsDeviation: boolean;
  createsCapa: boolean;
}

export interface OosListParams {
  status?: OosStatusValue;
  kind?: OosKindValue;
  batchRef?: string;
}

interface OosListResponse {
  items: OosListItem[];
  nextCursor: string | null;
}

interface QueryResult {
  data?: OosListResponse;
  isLoading: boolean;
  isError: boolean;
  refetch?: () => void;
}

function buildUrl(params: OosListParams): string {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.kind) search.set('kind', params.kind);
  if (params.batchRef) search.set('batchRef', params.batchRef);
  const qs = search.toString();
  return `/api/v1/oos${qs ? `?${qs}` : ''}`;
}

export function useOosListQuery(params: OosListParams): QueryResult {
  const [data, setData] = useState<OosListResponse | undefined>(undefined);
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(false);

  const url = useMemo(() => buildUrl(params), [params.status, params.kind, params.batchRef]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const resp = await fetch(apiUrl(url), { headers: buildHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = (await resp.json()) as OosListResponse;
      setData(json);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  return { data, isLoading, isError, refetch: fetchList };
}
